import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
// import { Alert, Linking } from 'react-native';
import { useChannelList } from './useChannelList';
import { useFeatureFlags } from './useFeatureFlags';

import { Notification } from '@cobu/types';
import { Strings } from '../constants/Strings';
import { showErrorMessage } from '../utils/messageToast';
import { logger } from '../utils/logger';

const strings = Strings.get();

export interface NotificationRouteHandler {
  navigateFromNotification: (notification: Notification) => void;
  navigateToPublicProfile: (userId: string) => void;
}

export function useNotificationRouting(): NotificationRouteHandler {
  const navigate = useNavigate();
  const { navigateToChannel } = useChannelList();
  const { showPlaces } = useFeatureFlags();

  const navigateToPublicProfile = useCallback((userId: string) => {
    if (!userId) return;
    navigate(`/public-profile/${userId}`);
  }, [navigate]);

  const navigateFromNotification = useCallback((notification: Notification) => {
    const link = notification.link;

    if (!link) return;

    switch (link.type) {
      case 'SCREEN': {
        const screen = (link.screenName || '').toLowerCase();
        const params = link.params || {};
        const metadata = notification.metadata || {};
        const commentId =
          params.commentId ||
          params.commentUuid ||
          params.commentID ||
          params.comment_id ||
          params.groupCommentId ||
          params.groupComment ||
          params.id ||
          metadata.commentId ||
          metadata.commentUuid ||
          metadata.comment_id ||
          (notification as any).groupComment;

        const withCommentParams = (path: string) => {
          if (!commentId) return path;
          const qs = new URLSearchParams({
            commentId: String(commentId),
            scrollToComment: 'true',
            source: 'notification',
          });
          return `${path}?${qs.toString()}`;
        };

        if (screen === 'chat') {
          console.log('🔗 [Notification Routing] Routing to chat screen:', {
            screen,
            commentId,
            hasCommentId: !!commentId,
            linkParams: params,
            notificationMetadata: metadata,
          });
          const target = withCommentParams('/chat');
          navigate(target);
          console.log('✅ [Notification Routing] Navigated to chat:', target);
          return;
        }

        if (screen === 'commentdetail') {
          // Place comments live on the place detail screen, not the comment
          // screens below, so this has to come first.
          if (params.placeId || params.placeUuid) {
            const placeId = params.placeId || params.placeUuid;
            navigate(showPlaces ? `/places/${placeId}` : '/');
            return;
          }

          if (params.groupId || params.groupUuid) {
            const id = params.groupId || params.groupUuid;
            navigate(withCommentParams(`/groups/${id}`));
            return;
          }

          if (params.eventId || params.groupEventId) {
            const id = params.eventId || params.groupEventId;
            navigate(withCommentParams(`/events/${id}`));
            return;
          }

          if (params.userId || params.userUuid) {
            const userId = params.userId || params.userUuid;
            navigate(`/marketplace/${userId}`);
            return;
          }

          navigate(withCommentParams('/chat'));
          return;
        }

        if (screen === 'perks') {
          navigate('/perks');
          return;
        }

        if (screen === 'groupdetail' || screen === 'group') {
          if (params.groupId || params.groupUuid) {
            const id = params.groupId || params.groupUuid;
            navigate(withCommentParams(`/groups/${id}`));
            return;
          }
        }

        if (screen === 'groupeventdetail' || screen === 'eventdetail' || screen === 'event') {
          if (params.eventId || params.groupEventId) {
            const id = params.eventId || params.groupEventId;
            navigate(withCommentParams(`/events/${id}`));
            return;
          }
        }

        if (screen === 'placedetail' || screen === 'place') {
          const placeId = params.placeId || params.placeUuid;
          if (placeId) {
            // No comment params: the place detail screen doesn't anchor to a
            // comment, so `withCommentParams` would only add dead query string.
            navigate(showPlaces ? `/places/${placeId}` : '/');
            return;
          }
        }

        if (screen === 'places') {
          navigate(showPlaces ? '/places' : '/');
          return;
        }

        if (screen === 'marketplace' || screen === 'marketplacedetail') {
          if (params.userId || params.userUuid) {
            const userId = params.userId || params.userUuid;
            navigate(`/marketplace/${userId}`);
          } else {
            navigate('/marketplace');
          }
          return;
        }

        if (screen === 'publicprofile' || screen === 'public-profile') {
          if (params.userId || params.userUuid) {
            const userId = params.userId || params.userUuid;
            navigate(`/public-profile/${userId}`);
            return;
          }
        }

        if (typeof params.path === 'string') {
          navigate(params.path);
          return;
        }

        return;
      }

      case 'DEEP_LINK': {
        const deeplink = link.deeplink || link.link;
        if (deeplink) {
          // Handle DM links that carry a userId
          const dmMatch = deeplink.match(/(?:cobu:\/\/)?chat\/dm\/([^\/?#]+)/);
          if (dmMatch) {
            const dmUserId = dmMatch[1];
            (async () => {
              try {
                const channel = await navigateToChannel(dmUserId, '');
                const allMembers = (await channel.queryMembers({})).members;
                const displayName = allMembers[0].user.name;
                if (channel?.id) {
                  navigate(`/chat/${channel.id}?title=${displayName}`);
                }
              } catch (err) {
                logger.log('Failed to navigate to channel:', err);
              }
            })();
            return;
          }
          try {
            navigate(deeplink as any);
          } catch {
            showErrorMessage(`${strings.common.error.title}: ${strings.common.error.linkOpenFailed}`);
          }
        }
        return;
      }

      case 'URL':
      default: {
        const url = link.link || link.deeplink;
        if (url) {
          try {
            window.open(url, '_blank');
          } catch (error) {
            logger.error('Failed to open URL:', error);
            showErrorMessage(`${strings.common.error.title}: ${strings.common.error.linkOpenFailed}`);
          }
        }
      }
    }
  }, [navigate, navigateToChannel, showPlaces]);

  return { navigateFromNotification, navigateToPublicProfile };
}
